import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { successToastMessage } from '../../../../components/layout/toast-root/toast';
import { useGetExerciseOptions } from '../../../../redux/api/exerciseConfigAPI';
import { useGetAvailableExercises } from '../../../../redux/api/exercisesAPI';
import { usePostPutProgramSet } from '../../../../redux/api/programsAPI';
import {
  useDeleteTrainingSet,
  useDeleteTrainingSetItem,
  usePostTrainingSetItem,
  usePutTrainingSet,
} from '../../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { FormMode, TrainingSetItemStatus } from '../../../../shared/constants';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { getProgramSetFromTrainingSet } from '../../../../utils/training/set-utils';

export function useCurrentTrainingSet(trainingSet: ITrainingSet) {
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [mode, setMode] = useState<FormMode>(FormMode.view);
  const [deleteTrainingSetItem] = useDeleteTrainingSetItem();
  const [updateTrainingSet] = usePutTrainingSet();
  const [createTrainingSetItem] = usePostTrainingSetItem();
  const [deleteTrainingSet] = useDeleteTrainingSet();
  const { data: availableExercises } = useGetAvailableExercises();
  const exercises = availableExercises || [];
  const [showNewExerciseModal, setShowNewExerciseModal] = useState(false);
  const [setDeleting, setSetDeleting] = useState(false);
  const [showSaveSetModal, setShowSaveSetModal] = useState(false);
  const { data: user } = useGetMyDetails();
  const [postPutProgramSet] = usePostPutProgramSet();

  const { t } = useTranslation();
  const trainingSetFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      exercise: exercises.find((e) => e.id === trainingSet.exerciseId),
    },
    onSubmit,
  });

  const onDeleteTrainingSetItem = useCallback(
    (item: ITrainingSetItem) => {
      deleteTrainingSetItem({
        trainingId: trainingSet.trainingId,
        trainingSetId: trainingSet.id,
        trainingSetItemId: item.id,
      });
    },
    [trainingSet?.trainingId, trainingSet?.id, deleteTrainingSetItem],
  );

  function onSubmit(values) {
    updateTrainingSet({
      trainingId: trainingSet.trainingId,
      trainingSetId: trainingSet.id,
      exerciseId: values.exercise?.id,
    })
      .unwrap()
      .then(() => {
        setMode(FormMode.view);
      })
      .catch((e) => console.log(e));
  }

  function onAddTrainingSetItemClick() {
    const lastItem = trainingSet.trainingSetItems.reduce((max, item) => {
      return item.orderNumber > max.orderNumber ? item : max;
    }, trainingSet.trainingSetItems[0]);

    const newTrainingSetItem = {
      orderNumber: lastItem.orderNumber + 1,
      rest: lastItem.rest,
      status: TrainingSetItemStatus.notStarted,
      trainingSetId: lastItem.trainingSetId,
      type: lastItem.type,
      trainingSetItemOptions: lastItem.trainingSetItemOptions.map((option) => ({
        optionId: option.optionId,
        programValue: option.programValue,
        programValue2: option.programValue2,
        value: null,
        value2: null,
      })),
    };

    createTrainingSetItem({
      trainingId: trainingSet.trainingId,
      trainingSetId: trainingSet.id,
      item: newTrainingSetItem,
    });
  }

  const notStartedItemsCount = useMemo(() => {
    return trainingSet.trainingSetItems.filter(
      (item) => item.status === TrainingSetItemStatus.notStarted,
    ).length;
  }, [trainingSet.trainingSetItems]);

  const finishedItemsCount = useMemo(() => {
    return trainingSet.trainingSetItems.filter(
      (item) => item.status === TrainingSetItemStatus.finished,
    ).length;
  }, [trainingSet.trainingSetItems]);

  const doLateItemsCount = useMemo(() => {
    return trainingSet.trainingSetItems.filter(
      (item) => item.status === TrainingSetItemStatus.doLate,
    ).length;
  }, [trainingSet.trainingSetItems]);

  const skippedItemsCount = useMemo(() => {
    return trainingSet.trainingSetItems.filter(
      (item) => item.status === TrainingSetItemStatus.skipped,
    ).length;
  }, [trainingSet.trainingSetItems]);

  function onDeleteTrainingSetConfirm() {
    deleteTrainingSet({
      trainingId: trainingSet.trainingId,
      trainingSetId: trainingSet.id,
    });
  }

  function onSaveSet(title: string) {
    const newProgramSet = getProgramSetFromTrainingSet(trainingSet);
    newProgramSet.title = title;
    newProgramSet.ownerId = user?.id;
    newProgramSet.setNumber = 0;
    newProgramSet.orderNumber = 0;
    newProgramSet.exercise = null;

    return postPutProgramSet({
      setId: undefined,
      body: newProgramSet,
    })
      .unwrap()
      .then((response) => {
        setShowSaveSetModal(false);
        successToastMessage(t('setTable.setSaved'));
        return response;
      })
      .catch((e) => {
        console.log(e);
      });
  }

  return {
    trainingSetFormik,
    exerciseOptions,
    mode,
    exercises,
    setMode,
    onDeleteTrainingSetItem,
    onAddTrainingSetItemClick,
    showNewExerciseModal,
    setShowNewExerciseModal,
    setDeleting,
    setSetDeleting,
    onDeleteTrainingSetConfirm,
    showSaveSetModal,
    setShowSaveSetModal,
    onSaveSet,
    finishedItemsCount,
    doLateItemsCount,
    skippedItemsCount,
    notStartedItemsCount,
  };
}
